/**
 * Registration pending page - shown after successful registration
 */

import { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Mail as EmailIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi, AuthApiError } from '../../shared/auth';

export function RegistrationPendingPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromParams);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async () => {
    if (!email.trim()) {
      setResendError(t('auth:verification.emailRequired', 'Email is required'));
      return;
    }

    setIsResending(true);
    setResendError(null);
    setResendMessage(null);

    try {
      const response = await authApi.resendVerification({ email: email.trim().toLowerCase() });
      setResendMessage(response.message);
    } catch (err) {
      if (err instanceof AuthApiError) {
        setResendError(err.message);
      } else {
        setResendError(t('auth:verification.resendFailed', 'Failed to resend verification email'));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[500px] w-full">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            <img src="/panluma-logo.png" alt={t('common:app.name')} className="h-24" />
          </div>

          <div className="flex justify-center">
            <EmailIcon className="h-16 w-16 text-primary" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold">
              {t('auth:verification.pendingTitle', 'Check Your Email')}
            </h2>
            <p className="text-muted-foreground text-sm">
              {emailFromParams
                ? t('auth:verification.pendingDescriptionWithEmail', {
                    email: emailFromParams,
                    defaultValue: `We've sent a verification link to ${emailFromParams}. Please check your inbox and click the link to verify your account.`,
                  })
                : t('auth:verification.pendingDescription', "We've sent a verification link to your email. Please check your inbox and click the link to verify your account.")}
            </p>
          </div>

          <Alert variant="default" className="bg-blue-50 border-blue-200 text-blue-800 text-left">
            <AlertDescription className="text-xs">
              {t(
                'auth:verification.pendingNote',
                "Didn't receive the email? Check your spam folder or request a new link below."
              )}
            </AlertDescription>
          </Alert>

          <div className="space-y-4 text-left">
            <h3 className="text-sm font-medium">
              {t('auth:verification.resendTitle', 'Resend Verification Email')}
            </h3>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs">{t('common:app.email', 'Email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isResending}
                className="h-9"
              />
            </div>
            <Button
              variant="outline"
              className="w-full h-9"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('auth:verification.resendButton', 'Resend Verification Email')
              )}
            </Button>
          </div>

          {resendMessage && (
            <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
              <AlertDescription className="text-xs">{resendMessage}</AlertDescription>
            </Alert>
          )}

          {resendError && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">{resendError}</AlertDescription>
            </Alert>
          )}

          <Button
            variant="link"
            asChild
            className="w-full text-xs"
          >
            <RouterLink to={emailFromParams ? `/login?email=${encodeURIComponent(emailFromParams)}` : '/login'}>
              {t('auth:verification.goToLogin', 'Go to Login')}
            </RouterLink>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
